/**
 * Sistema de Facciones — fuente de verdad única.
 *
 * Dos ciudades en guerra a muerte (Aresgard / Elvaria) + una tercera facción
 * técnica de Rebeldes (gris, free-for-all, sin lore). Un personaje SIN facción
 * (`faction` null/undefined) es un Newbie.
 *
 * Convención del proyecto: IDs internos en inglés, display strings en español.
 * Ver docs/FACTIONS.md.
 */

/** Identificador interno de facción (persistido en DB). */
export type FactionId = 'aresgard' | 'elvaria' | 'rebel';

export interface FactionDef {
  id: FactionId;
  /** Nombre para mostrar en español. */
  labelEs: string;
  /** Color de la facción (hex string CSS). Usado para nombres, markers, banners. */
  colorHex: string;
  /** True si es una de las dos ciudades en guerra (no rebeldes). */
  isCity: boolean;
}

export const FACTIONS: Record<FactionId, FactionDef> = {
  aresgard: { id: 'aresgard', labelEs: 'Aresgard', colorHex: '#d23b3b', isCity: true },
  elvaria: { id: 'elvaria', labelEs: 'Elvaria', colorHex: '#3b6fd2', isCity: true },
  rebel: { id: 'rebel', labelEs: 'Rebelde', colorHex: '#9a9a9a', isCity: false },
};

/** Color del nombre de un Newbie (sin facción). */
export const NEWBIE_COLOR_HEX = '#ffffff';

/**
 * Recompensa opcional al ascender a un rango. Se otorga en el Rey Faccionario.
 * Todos los campos opcionales; se aplican los que estén presentes.
 */
export interface FactionRankReward {
  exp?: number;
  gold?: number;
  itemId?: string;
}

export interface FactionRankDef {
  /** ID interno en inglés. */
  id: string;
  /** Nombre para mostrar en español. */
  labelEs: string;
  /** Enemigos de la ciudad enemiga eliminados (acumulado) requeridos para el rango. */
  killsRequired: number;
  /** Recompensa al ascender a este rango (opcional). */
  reward?: FactionRankReward;
}

/**
 * Escalafón de rangos por enemigos eliminados. El índice en el array es el
 * "nivel de rango" (0 = Recluta). El Héroe Nacional se muestra como "Héroe".
 *
 * NOTA: el ascenso es una acción deliberada ante el Rey Faccionario (no
 * automático). `killsRequired` es el umbral mínimo de `enemiesKilled` para
 * poder ascender a ese rango. (Futuro: además requerirá una quest — ver
 * docs/FACTIONS.md.)
 */
export const FACTION_RANKS: FactionRankDef[] = [
  { id: 'recruit', labelEs: 'Recluta', killsRequired: 0 },
  { id: 'soldier', labelEs: 'Soldado', killsRequired: 10, reward: { gold: 5000, exp: 5000 } },
  { id: 'veteran', labelEs: 'Veterano', killsRequired: 25, reward: { gold: 15000, exp: 15000 } },
  { id: 'corporal', labelEs: 'Cabo', killsRequired: 50, reward: { gold: 30000, exp: 40000 } },
  { id: 'sergeant', labelEs: 'Sargento', killsRequired: 100, reward: { gold: 60000, exp: 100000 } },
  // minimapa de aliados desde acá
  { id: 'lieutenant', labelEs: 'Teniente', killsRequired: 200, reward: { gold: 120000, exp: 250000 } },
  { id: 'captain', labelEs: 'Capitán', killsRequired: 400, reward: { gold: 250000, exp: 600000 } },
  { id: 'commander', labelEs: 'Comandante', killsRequired: 750, reward: { gold: 500000, exp: 1500000 } },
  { id: 'colonel', labelEs: 'Coronel', killsRequired: 1250, reward: { gold: 1000000, exp: 4000000 } },
  { id: 'general', labelEs: 'General', killsRequired: 2000, reward: { gold: 2000000, exp: 10000000 } },
  // "Héroe Nacional" → "Héroe"
  { id: 'hero', labelEs: 'Héroe', killsRequired: 5000, reward: { gold: 5000000, exp: 25000000 } },
];

/** Honor otorgado por cada enemigo de la ciudad enemiga eliminado. */
export const HONOR_PER_KILL = 100;

/** Nivel máximo de un Newbie (sin facción). Debe elegir ciudad para seguir. */
export const NEWBIE_LEVEL_CAP = 15;

/** Índice de rango (Teniente) desde el cual se ven aliados en el minimapa. */
export const MINIMAP_ALLY_RANK_INDEX = 5;

/** Índice del rango Héroe (último). La partícula permanente se aplica acá. */
export const HERO_RANK_INDEX = FACTION_RANKS.length - 1;

/** ID del FX de destello permanente del Héroe (catálogo de partículas). */
export const HERO_GLOW_FX_ID = 'fx_hero_glow';

// ============================================================================
// HELPERS PUROS (compartidos server + cliente)
// ============================================================================

/** Definición de facción, o null si el id no es válido / es newbie. */
export function getFactionDef(faction: FactionId | null | undefined): FactionDef | null {
  if (!faction) return null;
  return FACTIONS[faction] ?? null;
}

/** Color CSS del nombre/marker según facción (newbie = blanco). */
export function getFactionColor(faction: FactionId | null | undefined): string {
  return getFactionDef(faction)?.colorHex ?? NEWBIE_COLOR_HEX;
}

/** Nombre en español de la facción (newbie = ''). */
export function getFactionLabel(faction: FactionId | null | undefined): string {
  return getFactionDef(faction)?.labelEs ?? '';
}

/** True si es una facción de ciudad (aresgard/elvaria). */
export function isCityFaction(faction: FactionId | null | undefined): boolean {
  return getFactionDef(faction)?.isCity === true;
}

/** True si la facción es free-for-all (rebelde): pega y recibe de todos. */
export function isFreeForAll(faction: FactionId | null | undefined): boolean {
  return faction === 'rebel';
}

/**
 * True solo si ambas son ciudades distintas (Aresgard vs Elvaria). Los kills y
 * el honor solo cuentan entre ciudades enemigas (no contra rebeldes/newbies).
 */
export function areEnemyFactions(
  a: FactionId | null | undefined,
  b: FactionId | null | undefined
): boolean {
  return isCityFaction(a) && isCityFaction(b) && a !== b;
}

/**
 * Mayor índice de rango cuyo `killsRequired` ≤ kills. Útil para saber a qué
 * rango es elegible un personaje (no necesariamente el que tiene asignado).
 */
export function getRankIndexForKills(kills: number): number {
  let idx = 0;
  for (let i = 0; i < FACTION_RANKS.length; i++) {
    if (kills >= FACTION_RANKS[i].killsRequired) idx = i;
    else break;
  }
  return idx;
}

/** Definición de rango por índice (clamp a los límites del array). */
export function getRankByIndex(rankIndex: number): FactionRankDef {
  const clamped = Math.max(0, Math.min(rankIndex, FACTION_RANKS.length - 1));
  return FACTION_RANKS[clamped];
}

/** Siguiente rango a partir de `rankIndex`, o null si ya es Héroe. */
export function getNextRank(rankIndex: number): FactionRankDef | null {
  const next = rankIndex + 1;
  return next < FACTION_RANKS.length ? FACTION_RANKS[next] : null;
}

/** True si con `kills` el personaje puede ascender desde `currentRankIndex`. */
export function canAscendRank(currentRankIndex: number, kills: number): boolean {
  const next = getNextRank(currentRankIndex);
  return next !== null && kills >= next.killsRequired;
}

/** True si el rango es Héroe (último). */
export function isHeroRank(rankIndex: number): boolean {
  return rankIndex >= HERO_RANK_INDEX;
}
